/**
 * @file useOnScreen.js
 * @description True while the element is in (or near) the viewport. One home for the IntersectionObserver pattern:
 *   the home carousel idles off-screen with it, and LazyVideo loads and plays only when visible.
 *   Without IntersectionObserver support it reports `fallback` (true by default, so content still works; analytics pass
 *   false so an old browser or a test never records something it did not see).
 * @module hooks/useOnScreen
 */
import { useEffect, useState } from "react";

/**
 * @param {{ current: Element|null }} ref
 * @param {{ enabled?: boolean, threshold?: number, rootMargin?: string, fallback?: boolean }} [opts]
 * @returns {boolean}
 */
export function useOnScreen(ref, { enabled = true, threshold = 0, rootMargin = "0px", fallback = true } = {}) {
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setOnScreen(fallback); // no IO support: visible unless the caller asked otherwise
      return undefined;
    }
    const io = new IntersectionObserver(([entry]) => setOnScreen(!!entry?.isIntersecting), { threshold, rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, enabled, threshold, rootMargin, fallback]);
  return onScreen;
}
